import {
  ButtonType,
  ButtonWidget,
  TextLinkWidget
} from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { MapDispatchToProps } from '~commonInfra/utils/ConnectUtils';
import { DeprecatedInterpolatedString } from '~src/commonInfra/components/InterpolatedString';
import { L10n } from '~src/commonInfra/L10n/L10n';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { TooltipAnchor } from '~src/spacesPlatform/components/tooltip/TooltipAnchor';

import { Messages } from '../../codegen/Localize';
import { resumeMetricFromExplorer } from '../actions/UpdateMetricsActions';

export interface MetricSuspendedIndicatorOwnProps {
  metricId: string;
  metricRefreshedTimestamp: string;
  userCanResumeSuspendedMetric: boolean;
  isOwner: boolean;
}

export interface MetricSuspendedIndicatorDispatchProps {
  resumeMetricFromExplorer: (metricId: string, isOwner: boolean) => void;
}

export type MetricSuspendedIndicatorProps = MetricSuspendedIndicatorOwnProps & MetricSuspendedIndicatorDispatchProps;

export class MetricSuspendedIndicator extends React.PureComponent<MetricSuspendedIndicatorProps> {
  public render() {
    /* eslint-disable jsx-a11y/alt-text */
    return <TooltipAnchor tooltipContent={this.renderTooltipContent}
      useKeyboardToActivateTooltip={true}
      width={228}
      disableAutoFocus={!this.props.userCanResumeSuspendedMetric}
      title={Messages.viewTooltipTitle()}>
      <img
        tabIndex={-1} // disable tabbing in datagrid cells, leaving focus managed by datagrid itself
        src={imgPath('server_alert_list_suspended.svg')}
        // Adding non-empty alt text causes VoiceOver to read "[alt text] and 1 more item" where "1 more item"
        // is the visual "Refresh suspended" text. Empty alt text will cause VoiceOver to read only
        // "Refresh suspended".
        alt={''}
        // Title is read by VoiceOver when focusing on the icon
        title={Messages.metricSuspendedRefreshIcon()}
        style={{ verticalAlign: 'middle', marginRight: '4px' }}
        data-tb-test-id="metric-suspended-icon"
      />
    </TooltipAnchor>;
  }

  private renderTooltipContent = (): JSX.Element => {
    if (this.props.userCanResumeSuspendedMetric) {
      return (
        <div data-tb-test-id="metrics-suspended-indicator">
          <div>
            <DeprecatedInterpolatedString
              baseStringKey="metricSuspendedWithResumeTooltip"
              translate={() => Messages.metricSuspendedWithResumeTooltip({LEARN_MORE_LINK: '{LEARN_MORE_LINK}'})}
              replacements={{
                'SUSPEND_DATE': () => <span>{L10n.localizedDate(this.props.metricRefreshedTimestamp)}</span>,
                'LEARN_MORE_LINK': () =>
                  <TextLinkWidget
                    href={HelpService.getServerHelpUrlByCategory('metrics-suspend')}
                    target="_blank"
                    testId="metrics-resume-help"
                  >
                    {Messages.learnMore()}
                  </TextLinkWidget>
              }}
            />
          </div>
          <ButtonWidget
            buttonType={ButtonType.Outline}
            testId="metrics-resume"
            style={{ marginTop: CommonStyles.Sizes.MediumSpace }}
            onClick={() => this.props.resumeMetricFromExplorer(this.props.metricId, this.props.isOwner)}
          >
            {Messages.metricResumeButtonLabel()}
          </ButtonWidget>
        </div>
      );
    } else {
      return <span>{Messages.metricSuspendedNoResumeTooltip()}</span>;
    }
  };
}

const mapDispatchToProps: MapDispatchToProps<MetricSuspendedIndicatorDispatchProps> = (dispatch) => bindActionCreators({
  resumeMetricFromExplorer
}, dispatch);

export const ConnectedMetricSuspendedIndicator = connect(undefined, mapDispatchToProps)(MetricSuspendedIndicator);

